/**
 * Салбарын самбар — салбарын ажилтны `/` (шийдвэр 2026-09-27).
 *
 * Компанийн самбар (`dashboard.tsx`) нь бүх салбар, агуулах, ханш,
 * нийлүүлэгчийн зээлийг харуулдаг — салбарын ажилтанд хэрэггүй, мөн
 * компанийн нийт орлого тэдэнд нээлттэй байх ёсгүй. Энд ЗӨВХӨН өөрийн
 * салбар: размер бүрийн нөөц, өнөөдрийн ба сарын орлого, агуулахаас
 * ирсэн гутал, өрсөлдөөн дэх байр.
 *
 * ⚠️ Юу ч ТООЦООЛОХГҮЙ — бүх тоо `/reports/branch-dashboard`-аас.
 *    «Цөөн размер» дүрэм нь `@boots/domain` → `sizeStockRows`.
 *
 * Анхааруулга (дууссан размер, 0₮, замд яваа) нь header-ийн хонхонд —
 * `branchAlertsFrom()` → `AppShell`.
 */

import { useState } from 'react';
import { formatDate, formatQty } from '@boots/domain';
import { useBranchDashboard } from '../lib/use-branch-dashboard';
import type { BranchDashboardData } from '../lib/types';
import { EmptyState, ErrorState } from '../components/empty-state';
import { Section } from '../components/section';
import { Tabs } from '../components/ui';
import { ColumnChart } from '../components/stats/column-chart';
import { HeroBand } from '../components/dashboard/hero-band';
import { DashboardSkeleton } from '../components/dashboard/dashboard-skeleton';
import { LeaderboardCard } from '../components/dashboard/leaderboard-card';
import { BranchKpis } from '../components/branch/branch-kpis';
import { SizeStockCard } from '../components/branch/size-stock-card';

export function BranchDashboardPage() {
  // undefined = серверийн анхдагч (өөрийн салбар) — хонхтой нэг хариу
  const [storeId, setStoreId] = useState<string>();
  const { data, isPending, error, refetch } = useBranchDashboard(storeId);

  if (isPending) return <DashboardSkeleton />;
  if (error) {
    return (
      <div className="rounded-card border border-hair bg-panel">
        <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const d = data.data;

  return (
    <div className="flex flex-col gap-3">
      <HeroBand />

      {d.store === null ? (
        <div className="rounded-card border border-hair bg-panel">
          <EmptyState
            title="Танд салбар оноогдоогүй байна"
            description="Админ «Ажилтан» тохиргоон дээр таны салбарыг сонгосны дараа энд салбарын нөөц, орлого гарна."
          />
        </div>
      ) : (
        <BranchBody d={d} storeId={storeId ?? d.store.id} onStore={setStoreId} />
      )}
    </div>
  );
}

function BranchBody({
  d, storeId, onStore,
}: {
  d: BranchDashboardData;
  storeId: string;
  onStore: (id: string) => void;
}) {
  return (
    <>
      {/* Нэг менежер хэд хэдэн салбар хариуцдаг (`stores.manager_id`) */}
      {d.stores.length > 1 ? (
        <Tabs
          tabs={d.stores.map((s) => ({ key: s.id, label: s.name }))}
          value={storeId}
          onChange={onStore}
        />
      ) : null}

      <BranchKpis d={d} />

      <SizeStockCard sizes={d.sizes} />

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <Section title="Өдөр бүрийн борлуулалт" subtitle={`энэ сар · ${formatQty(d.sales.month.units)} ш`}>
          <ColumnChart
            ariaLabel="Энэ сарын өдөр бүр зарагдсан гутал"
            // Сүүлийн багана = өнөөдөр
            highlight={d.sales.daily.length - 1}
            points={d.sales.daily.map((p) => ({
              key: p.date,
              label: String(Number(p.date.slice(8))),
              title: formatDate(p.date),
              value: p.units,
            }))}
          />
        </Section>

        <InTransit rows={d.inTransit} />
      </div>

      <LeaderboardCard myStoreId={d.store.id} />
    </>
  );
}

/** Энэ салбар руу илгээгдсэн, хүлээн аваагүй шилжүүлэг — ирмэгц уншуулна. */
function InTransit({ rows }: { rows: BranchDashboardData['inTransit'] }) {
  const total = rows.reduce((n, t) => n + t.count, 0);
  return (
    <Section
      title="Замд ирж яваа"
      subtitle={rows.length > 0 ? `${formatQty(total)} ш · ${formatQty(rows.length)} шилжүүлэг` : undefined}
    >
      {rows.length === 0 ? (
        <EmptyState
          compact
          title="Замд шилжүүлэг алга"
          description="Агуулах гутал илгээмэгц энд гарна. Ирэхэд гар утсаар уншуулж хүлээн авна."
        />
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((t) => (
            <li key={t.id} className="flex items-baseline gap-3 rounded-well px-2 py-2 hover:bg-row-hover">
              <span className="flex min-w-0 flex-col">
                <span className="num text-meta">{t.code}</span>
                <span className="truncate text-micro text-ink-3">
                  {t.from}
                  {t.sentAt ? ` · ${formatDate(t.sentAt)} илгээсэн` : ''}
                </span>
              </span>
              <span className="num ml-auto text-h3 whitespace-nowrap">{formatQty(t.count)} ш</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
